import { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { logger } from '../utils/logger';
import { isErrorWithMessage } from '../types/common';
import toast from 'react-hot-toast';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showReload?: boolean;
  context?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  errorId: string;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      errorId: '',
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    const errorId = `ERR_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    return {
      hasError: true,
      error,
      errorId,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details for debugging (only in development)
    logger.error(
      'Error caught by boundary',
      isErrorWithMessage(error) ? error : undefined,
      {
        component: 'ErrorBoundary',
        operation: 'componentDidCatch',
        extra: {
          context: this.props.context || 'Unknown',
          componentStack: errorInfo.componentStack,
          errorBoundary: true,
        },
      }
    );

    this.setState({
      error,
      errorInfo,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Show toast notification
    toast.error(
      `Wystąpił błąd w aplikacji${this.props.context ? ` (${this.props.context})` : ''}. Spróbuj odświeżyć stronę.`,
      {
        duration: 5000,
        id: this.state.errorId, // Prevent duplicate toasts
      }
    );

    // Log error to external service (this would normally go to a service like Sentry)
    this.logErrorToService(error, errorInfo);
  }

  private async logErrorToService(error: Error, errorInfo: ErrorInfo) {
    try {
      // Get user ID securely from Firebase Auth instead of localStorage
      const userId = this.getCurrentUserIdSecure();

      const errorData = {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        context: this.props.context || 'Unknown',
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        userId: userId || 'anonymous',
      };

      // Send to Sentry
      Sentry.captureException(error, {
        contexts: {
          react: { componentStack: errorInfo.componentStack },
          app: {
            context: this.props.context || 'Unknown',
            errorId: this.state.errorId,
          },
        },
        user: { id: errorData.userId },
      });

      logger.debug('Error reported to Sentry', errorData, {
        component: 'ErrorBoundary',
        operation: 'logErrorToService',
      });
    } catch (loggingError) {
      logger.error(
        'Failed to log error to service',
        isErrorWithMessage(loggingError) ? loggingError : undefined,
        {
          component: 'ErrorBoundary',
          operation: 'logErrorToService',
        }
      );
    }
  }

  private handleReload = () => {
    window.location.reload();
  };

  private getCurrentUserIdSecure(): string | null {
    try {
      // Import Firebase auth dynamically to avoid circular dependencies
      const { getCurrentUserId } = require('../firebase');
      return getCurrentUserId();
    } catch {
      // If Firebase auth fails, return null instead of exposing data
      return null;
    }
  }

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      errorId: '',
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
              <div className="text-center">
                {/* Error Icon */}
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                  <svg
                    className="h-6 w-6 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>

                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Wystąpił nieoczekiwany błąd
                </h3>

                <p className="text-sm text-gray-500 mb-6">
                  {this.props.context
                    ? `Błąd w sekcji: ${this.props.context}. Przepraszamy za niedogodności.`
                    : 'Przepraszamy za niedogodności. Spróbuj ponownie.'}
                </p>

                {/* Error ID for support */}
                <p className="text-xs text-gray-400 mb-6 font-mono">
                  ID błędu: {this.state.errorId}
                </p>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <button
                    onClick={this.handleRetry}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                  >
                    Spróbuj ponownie
                  </button>

                  {this.props.showReload !== false && (
                    <button
                      onClick={this.handleReload}
                      className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                    >
                      Odśwież stronę
                    </button>
                  )}

                  <button
                    onClick={() => window.history.back()}
                    className="w-full flex justify-center py-2 px-4 text-sm font-medium text-gray-500 hover:text-gray-700"
                  >
                    Wróć do poprzedniej strony
                  </button>
                </div>

                {/* Development Error Details */}
                {import.meta.env.MODE === 'development' && this.state.error && (
                  <details className="mt-6 text-left">
                    <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                      Szczegóły błędu (tryb deweloperski)
                    </summary>
                    <div className="mt-3 p-3 bg-gray-100 rounded text-xs">
                      <div className="mb-2">
                        <strong>Błąd:</strong>
                        <pre className="mt-1 text-red-600 whitespace-pre-wrap">
                          {this.state.error.message}
                        </pre>
                      </div>

                      {this.state.error.stack && (
                        <div className="mb-2">
                          <strong>Stack trace:</strong>
                          <pre className="mt-1 text-gray-600 whitespace-pre-wrap text-xs overflow-auto max-h-32">
                            {this.state.error.stack}
                          </pre>
                        </div>
                      )}

                      {this.state.errorInfo?.componentStack && (
                        <div>
                          <strong>Component stack:</strong>
                          <pre className="mt-1 text-gray-600 whitespace-pre-wrap text-xs overflow-auto max-h-32">
                            {this.state.errorInfo.componentStack}
                          </pre>
                        </div>
                      )}
                    </div>
                  </details>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
